import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

const SensorsPage = () => {
  const [sensors, setSensors] = useState([]);
  const [newSensor, setNewSensor] = useState({
    name: '',
    identifier: '',
    bpfFilters: []
  });
  const [editingSensor, setEditingSensor] = useState(null);
  const [editedSensor, setEditedSensor] = useState({
    name: '',
    identifier: '',
    bpfFilters: []
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState('');
  const [sensorSearch, setSensorSearch] = useState('');
  const { API_BASE_URL } = useAuth();

  // Exemplo de presets de filtros BPF
  const bpfPresets = [
    'tcp port 80 or tcp port 443',
    'udp port 53 or tcp port 53',
    'icmp',
    'not (src net 10.0.0.0/8 or src net 172.16.0.0/12 or src net 192.168.0.0/16)',
    'tcp port 445 or tcp port 139'
  ];

  useEffect(() => {
    fetchSensors();
  }, []);

  const fetchSensors = () => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + 'sensors', {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => setSensors(data))
      .catch((err) => console.error(err));
  };

  const handleRegisterSensor = () => {
    if (!/^[a-zA-Z0-9_]+$/.test(newSensor.name)) {
      alert('Sensor name can only contain letters, numbers, and the "_" character.');
      return;
    }
    if (!newSensor.identifier) {
      alert('Sensor Identifier is required.');
      return;
    }

    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + 'sensors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(newSensor),
    })
      .then((res) => res.json())
      .then(() => {
        fetchSensors();
        setNewSensor({ name: '', identifier: '', bpfFilters: [] });
        setAddModalOpen(false);
      })
      .catch((err) => console.error(err));
  };

  const handleEditSensor = (sensor) => {
    setEditingSensor(sensor);
    setEditedSensor({
      name: sensor.name,
      identifier: sensor.identifier,
      bpfFilters: sensor.bpfFilters || []
    });
  };

  const handleSaveEditSensor = () => {
    if (!/^[a-zA-Z0-9_]+$/.test(editedSensor.name)) {
      alert('Sensor name can only contain letters, numbers, and the "_" character.');
      return;
    }
    if (!editedSensor.identifier) {
      alert('Sensor Identifier is required.');
      return;
    }

    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + `sensors/${editingSensor.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(editedSensor),
    })
      .then(() => {
        fetchSensors();
        setEditingSensor(null);
      })
      .catch((err) => console.error(err));
  };

  const handleRemoveSensor = (sensorId) => {
    if (window.confirm('Are you sure you want to remove this sensor?')) {
      const token = localStorage.getItem('token');
      fetch(API_BASE_URL + `sensors/${sensorId}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      })
        .then(() => fetchSensors())
        .catch((err) => console.error(err));
    }
  };

  const handleSensorAction = (sensorId, action) => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + `sensors/${sensorId}/${action}`, {
      method: 'POST',
      headers: { Authorization: token },
    })
      .then(() => fetchSensors())
      .catch((err) => console.error(err));
  };

  // Adicionar filtro ao novo sensor
  const addFilterToNewSensor = () => {
    if (!currentFilter.trim()) {
      alert('Filter expression cannot be empty.');
      return;
    }

    setNewSensor({
      ...newSensor,
      bpfFilters: [...newSensor.bpfFilters, currentFilter.trim()]
    });
    setCurrentFilter('');
  };

  // Adicionar filtro ao sensor em edição
  const addFilterToEditSensor = () => {
    if (!currentFilter.trim()) {
      alert('Filter expression cannot be empty.');
      return;
    }

    setEditedSensor({
      ...editedSensor,
      bpfFilters: [...editedSensor.bpfFilters, currentFilter.trim()]
    });
    setCurrentFilter('');
  };

  // Remover filtro do novo sensor
  const removeFilterFromNewSensor = (index) => {
    const updatedFilters = [...newSensor.bpfFilters];
    updatedFilters.splice(index, 1);
    setNewSensor({
      ...newSensor,
      bpfFilters: updatedFilters
    });
  };

  // Remover filtro do sensor em edição
  const removeFilterFromEditSensor = (index) => {
    const updatedFilters = [...editedSensor.bpfFilters];
    updatedFilters.splice(index, 1);
    setEditedSensor({
      ...editedSensor,
      bpfFilters: updatedFilters
    });
  };

  const applyPreset = (preset) => {
    setCurrentFilter(preset);
  };

  const formatLastPing = (timestamp) => {
    if (!timestamp) return "Never";

    // Create a date object from the timestamp
    const date = new Date(timestamp);

    // Format the date to show only up to seconds
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Helper function to format the uptime in days, hours, minutes, seconds
  const formatUptime = (seconds) => {
    if (!seconds) return "0s";

    // Calculate days, hours, minutes, and seconds
    const days = Math.floor(seconds / (24 * 60 * 60));
    seconds -= days * 24 * 60 * 60;

    const hours = Math.floor(seconds / (60 * 60));
    seconds -= hours * 60 * 60;

    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;
    seconds = Math.floor(seconds);

    // Build the formatted string, skipping zero values from the left
    let uptimeString = "";

    if (days > 0) {
      uptimeString += `${days}d `;
    }

    if (hours > 0 || (days > 0 && (minutes > 0 || seconds > 0))) {
      uptimeString += `${hours}h `;
    }

    if (minutes > 0 || (days > 0 || hours > 0) && seconds > 0) {
      uptimeString += `${minutes}m `;
    }

    if (seconds > 0 || uptimeString === "") {
      uptimeString += `${seconds}s`;
    }

    return uptimeString;
  };

  // Componente para o painel de filtros BPF (reutilizado em modais)
  const BPFFiltersPanel = ({ filters, onAdd, onRemove, mode }) => {
    const isNewMode = mode === 'new';

    return (
      <div className="card mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0">BPF Filters</h6>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Enter BPF filter expression"
                value={currentFilter}
                onChange={(e) => setCurrentFilter(e.target.value)}
              />
              <button
                className="btn btn-primary"
                onClick={onAdd}
              >
                Add Filter
              </button>
            </div>

            <div className="mt-2">
              <small className="text-muted">Quick Presets:</small>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {bpfPresets.map((preset, index) => (
                  <button
                    key={index}
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.length > 15 ? preset.substring(0, 15) + '...' : preset}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filters.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th>Filter Expression</th>
                    <th style={{ width: '10%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filters.map((filter, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td><code>{filter}</code></td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => onRemove(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info">
              No BPF filters added yet.
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredSensors = sensors.filter(sensor => 
    sensor.name.toLowerCase().includes(sensorSearch.toLowerCase()) ||
    sensor.identifier.toLowerCase().includes(sensorSearch.toLowerCase())
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Sensors</h2>
        <div className="input-group" style={{ maxWidth: '300px' }}>
          <span className="input-group-text">Filter</span>
          <input
            type="text"
            className="form-control"
            placeholder="Search sensors..."
            value={sensorSearch}
            onChange={(e) => setSensorSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="mb-3">
        <button
          className="btn btn-primary"
          onClick={() => setAddModalOpen(true)}
        >
          Register Sensor
        </button>
      </div>

      <div className="mb-3">
        <table className="table">
          <thead>
            <tr>
              <th>Sensor Name</th>
              <th>Identifier</th>
              <th>Last Ping</th>
              <th>Uptime</th>
              <th>BPF Filters</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSensors.map((sensor) => (
              <tr key={sensor.id}>
                <td>{sensor.name}</td>
                <td>{sensor.identifier}</td>
                <td>{formatLastPing(sensor.last_ping)}</td>
                <td>{formatUptime(sensor.uptime)}</td>
                <td>
                  <span className="badge bg-info">
                    {sensor.bpfFilters ? sensor.bpfFilters.length : 0} filters
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-warning me-2"
                    onClick={() => handleEditSensor(sensor)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-delete"
                    style={{ backgroundColor: '#950606', color: 'white', margin: 5 }}
                    onClick={() => handleRemoveSensor(sensor.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Sensor Modal */}
      {addModalOpen && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Register Sensor</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAddModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Sensor Name</label>
                  <input
                    className="form-control"
                    value={newSensor.name}
                    onChange={(e) =>
                      setNewSensor({ ...newSensor, name: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Sensor Identifier</label>
                  <input
                    className="form-control"
                    value={newSensor.identifier}
                    onChange={(e) =>
                      setNewSensor({ ...newSensor, identifier: e.target.value })
                    }
                  />
                </div>

                {/* BPF Filters Panel */}
                <BPFFiltersPanel
                  filters={newSensor.bpfFilters}
                  onAdd={addFilterToNewSensor}
                  onRemove={removeFilterFromNewSensor}
                  mode="new"
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleRegisterSensor}>
                  Register
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setAddModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sensor Modal */}
      {editingSensor && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Sensor</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditingSensor(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Sensor Name</label>
                  <input
                    className="form-control"
                    value={editedSensor.name}
                    onChange={(e) =>
                      setEditedSensor({ ...editedSensor, name: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Sensor Identifier</label>
                  <input
                    className="form-control"
                    value={editedSensor.identifier}
                    onChange={(e) =>
                      setEditedSensor({
                        ...editedSensor,
                        identifier: e.target.value,
                      })
                    }
                  />
                </div>

                {/* BPF Filters Panel */}
                <BPFFiltersPanel
                  filters={editedSensor.bpfFilters}
                  onAdd={addFilterToEditSensor}
                  onRemove={removeFilterFromEditSensor}
                  mode="edit"
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEditSensor}
                >
                  Save Changes
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditingSensor(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorsPage;
